import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMultiCmt } from '../contexts/MultiCmtContext';
import { useFirestore } from '../contexts/FirestoreContext';

import { auth } from '../config/firebase';
import { doc, getDoc, collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { Button, Spin, Alert, Card, Typography, Tag, Space, message, Modal, Timeline, List, Drawer, Tooltip, Popconfirm } from 'antd';
import { SaveOutlined, DownloadOutlined, SecurityScanOutlined, EditOutlined, CheckCircleOutlined, HistoryOutlined, ReloadOutlined, ExclamationCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

/**
 * BusinessContinuityViewer Component - Secure in-app PDF plan editing with CMT isolation
 * Features: iframe communication, autosave, security validation, error handling
 * USES CRISIS PLAN'S EXACT MODAL APPROACH FOR CONSISTENCY
 */
export default function BusinessContinuityViewer() {
  const { cmtId: urlCmtId } = useParams();
  const navigate = useNavigate();
  // Fixed planKey for Business Continuity Plan
  const planKey = 'business_continuity_plan';
  
  const { activeCmt } = useMultiCmt();
  const { db } = useFirestore();
  const iframeRef = useRef(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);
  const [fieldCount, setFieldCount] = useState(0);
  
  // Template loading state
  const [templateUrl, setTemplateUrl] = useState(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true);
  const [error, setError] = useState(null);
  
  // 🎯 PRODUCTION FEATURES STATE - COPIED FROM CRISIS PLAN
  // Version history and audit
  const [versionHistory, setVersionHistory] = useState([]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [currentETag, setCurrentETag] = useState(null);
  
  // Save and conflict management
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [conflictInfo, setConflictInfo] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Error handling and feedback
  const [errorHistory, setErrorHistory] = useState([]);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  
  // Auto-save configuration
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  
  // Plan configuration
  const isSaving = saveStatus === 'saving';
  const planTitle = 'Business Continuity Plan';

  // Close handler - navigate back to My Crisis Resources
  const onClose = useCallback(() => {
    navigate('/my-crisis-resources');
  }, [navigate]);

  // 🎯 CRISIS PLAN'S EXACT SAVE APPROACH - COPIED IDENTICALLY
  const triggerSave = useCallback(async () => {
    if (!iframeRef.current || !pdfReady) {
      throw new Error('PDF not ready for saving');
    }
    
    try {
      setSaveStatus('saving');
      console.log('🎯 Triggering Business Continuity Plan save from parent component...');
      
      // Send save request to iframe - SAME AS CRISIS PLAN
      iframeRef.current.contentWindow.postMessage({
        type: 'PDF_SAVE_REQUEST'
      }, window.location.origin);
      
      // Wait for save completion or error - SAME AS CRISIS PLAN
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Save operation timed out'));
        }, 60000); // 60 second timeout
        
        const handleSaveResponse = (event) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'PDF_SAVE_ERROR') {
            clearTimeout(timeout);
            window.removeEventListener('message', handleSaveResponse);
            setSaveStatus('error');
            addError('Save failed', event.data.error);
            reject(new Error(event.data.error));
          } else if (event.data.type === 'PDF_SAVE_SUCCESS') {
            clearTimeout(timeout);
            window.removeEventListener('message', handleSaveResponse);
            setSaveStatus('saved');
            setLastSavedAt(new Date());
            setHasUnsavedChanges(false);
            message.success('Business Continuity Plan saved successfully!');
            resolve(event.data);
          }
        };
        
        window.addEventListener('message', handleSaveResponse);
      });
      
    } catch (error) {
      setSaveStatus('error');
      addError('Save orchestration failed', error);
      throw error;
    }
  }, [pdfReady]);

  // 🎯 CRISIS PLAN'S ERROR MANAGEMENT - COPIED IDENTICALLY
  const addError = useCallback((message, error) => {
    const errorEntry = {
      id: Date.now(),
      message,
      error: error?.message || error,
      timestamp: new Date(),
      stack: error?.stack
    };
    
    setErrorHistory(prev => [errorEntry, ...prev.slice(0, 9)]); // Keep last 10 errors
    console.error(`❌ ${message}:`, error);
  }, []);

  // 🔧 Load template URL using SAME PROXY APPROACH as Crisis Plan
  useEffect(() => {
    const loadTemplate = async () => {
      if (!planKey || !activeCmt?.id) return;
      
      try {
        setIsLoadingTemplate(true);
        console.log('🔍 Loading Business Continuity Plan template using proxy approach for planKey:', planKey);
        
        // Validate authentication for proxy - EXACT SAME as Crisis Plan
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) {
          throw new Error('User not authenticated for business continuity plan access');
        }
        
        try {
          // Test authentication by getting ID token
          await firebaseUser.getIdToken();
          
          // Use DEDICATED proxy endpoint for Business Continuity Plan
          const proxyUrl = `/api/business-continuity-plan-pdf?planKey=${encodeURIComponent(planKey)}&cmtId=${encodeURIComponent(activeCmt?.id || '')}`;
          console.log('✅ Business Continuity Plan proxy URL configured:', proxyUrl);
          
          setTemplateUrl(proxyUrl);
          setError(null);
          setIsLoadingTemplate(false);
          return;
          
        } catch (authError) {
          console.error('❌ Authentication error for business continuity plan proxy:', authError);
          throw new Error(`Authentication failed: ${authError.message}`);
        }
        
      } catch (err) {
        console.error('❌ Error loading Business Continuity Plan template:', err);
        setError(err.message);
      } finally {
        setIsLoadingTemplate(false);
      }
    };

    loadTemplate();
  }, [planKey, activeCmt?.id]);

  // 🔧 Load version history for Business Continuity Plan
  useEffect(() => {
    if (!activeCmt?.id || !db) return;

    console.log('🔍 Setting up Business Continuity Plan version history listener...');
    
    const versionsRef = collection(db, 'business_continuity_plan_versions');
    const versionsQuery = query(
      versionsRef,
      where('planKey', '==', 'business_continuity_plan'),
      where('cmtId', '==', activeCmt.id),
      orderBy('version', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(versionsQuery, (snapshot) => {
      const versions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().createdAt?.toDate() || new Date(),
        createdBy: doc.data().createdBy || 'Unknown'
      }));
      
      console.log('📚 Business Continuity Plan version history updated:', versions.length, 'versions');
      setVersionHistory(versions);
      
      if (versions.length > 0) {
        setCurrentVersion(versions[0].version);
      }
    }, (error) => {
      console.error('❌ Error loading Business Continuity Plan version history:', error);
    });

    return unsubscribe;
  }, [activeCmt?.id, db]);

  // 🔧 Handle iframe load
  const handleIframeLoad = useCallback(() => {
    console.log('📄 Business Continuity Plan PDF iframe loaded');
    setIframeLoaded(true);
    setPdfReady(true);
    setFieldCount(8); // Default field count for Business Continuity Plan
  }, []);

  /**
   * Handle messages from PDF iframe with enhanced security - COPIED FROM CRISIS PLAN
   */
  const handleIframeMessage = useCallback(async (event) => {
    // Security: Validate origin
    if (event.origin !== window.location.origin) {
      console.warn('Rejected iframe message from unauthorized origin:', event.origin);
      return;
    }

    // 🎯 CRISIS PLAN'S ID TOKEN HANDLING - COPIED IDENTICALLY
    if (event.data.type === 'REQUEST_ID_TOKEN') {
      console.log('🎯 ID token requested by business continuity plan iframe');
      
      try {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) {
          throw new Error('User not authenticated');
        }
        
        const idToken = await firebaseUser.getIdToken();
        
        // Send token back to iframe
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({
            type: 'ID_TOKEN_RESPONSE',
            token: idToken
          }, window.location.origin);
          
          console.log('✅ ID token sent to business continuity plan iframe');
        }
        
      } catch (error) {
        console.error('❌ Failed to get ID token for iframe:', error);
        
        // Send error response
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({
            type: 'ID_TOKEN_RESPONSE',
            token: null,
            error: error.message
          }, window.location.origin);
        }
      }
      
      return;
    }

    // Security: Validate source
    if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
      console.warn('Rejected iframe message from unauthorized source');
      return;
    }

    const { type, field, value, planKey: msgPlanKey, cmtId, fieldCount: detectedFields } = event.data;

    // Validate plan context
    if (msgPlanKey !== planKey || cmtId !== activeCmt?.id) {
      console.warn('Message context mismatch:', { msgPlanKey, planKey, cmtId, currentCmtId: activeCmt?.id });
      return;
    }

    // 🎯 CRISIS PLAN'S MESSAGE HANDLING - COPIED IDENTICALLY
    switch (type) {
      case 'PDF_READY':
        setPdfReady(true);
        // Send current field values to PDF
        sendMessageToIframe({
          type: 'PDF_APPLY_VALUES',
          data: {
            fields: getCurrentFields()
          }
        });
        break;

      case 'PDF_FIELDS_DETECTED':
        setFieldCount(detectedFields || 0);
        break;

      case 'PDF_FORM_CHANGE':
        if (field && hasAccess) {
          await handleFieldChange(field, value);
          setLastSaved(new Date());
        }
        break;

      case 'PDF_CURRENT_VALUES':
        // Handle current values response if needed
        break;

      default:
        console.warn('Unknown iframe message type:', type);
    }
  }, [planKey, activeCmt?.id]);

  // 🎯 CRISIS PLAN'S HELPER FUNCTIONS - COPIED IDENTICALLY
  const sendMessageToIframe = useCallback((message) => {
    if (iframeRef.current && iframeRef.current.contentWindow && iframeLoaded) {
      try {
        iframeRef.current.contentWindow.postMessage(message, window.location.origin);
      } catch (error) {
        console.error('Failed to send message to iframe:', error);
      }
    }
  }, [iframeLoaded]);

  const getCurrentFields = useCallback(() => {
    // Return current field values - Crisis Plan approach
    return {};
  }, []);

  const handleFieldChange = useCallback(async (field, value) => {
    // Handle field changes - Crisis Plan approach
    console.log('Field changed:', field, value);
    setHasUnsavedChanges(true);
  }, []);

  // Access control - Crisis Plan approach
  const hasAccess = true; // Simplified for now

  // Set up iframe message listener - EXACT REPLICA
  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [handleIframeMessage]);

  // 🔧 Export functionality - adapted for Business Continuity Plan
  const handleExport = useCallback(async () => {
    try {
      console.log('📥 Initiating Business Continuity Plan download...');
      
      if (!activeCmt?.id) {
        message.error('CMT information not available for download');
        return;
      }
      
      // Get Firebase ID token for authentication
      const currentUser = auth.currentUser;
      if (!currentUser) {
        message.error('Authentication required for download');
        return;
      }
      
      const idToken = await currentUser.getIdToken();
      
      // Use unified download endpoint - IDENTICAL to Crisis Plan approach
      const downloadUrl = `/api/download-plan?planKey=business_continuity_plan&cmtId=${encodeURIComponent(activeCmt.id)}`;
      
      // Create authenticated fetch request for download
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Business Continuity Plan - ${activeCmt?.name || 'Current CMT'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Business Continuity Plan download completed');
      message.success('Business Continuity Plan downloaded successfully');
      
    } catch (error) {
      console.error('❌ Error downloading Business Continuity Plan:', error);
      message.error('Failed to download Business Continuity Plan. Please try again.');
    }
  }, [activeCmt]);

  // 🔧 Conflict resolution
  const resolveConflict = useCallback(async (strategy) => {
    try {
      setShowConflictModal(false);
      setConflictInfo(null);
      
      if (strategy === 'refresh') {
        // Refresh the page to get latest version
        window.location.reload();
      } else if (strategy === 'overwrite') {
        // Force save with current version
        await triggerSave();
      }
    } catch (error) {
      addError('Business Continuity Plan conflict resolution failed', error);
    }
  }, [triggerSave, addError]);

  // 🔧 Version rollback functionality
  const handleVersionRollback = useCallback(async (version) => {
    try {
      console.log('🔄 Rolling back Business Continuity Plan to version:', version.version);
      
      // This would implement version rollback logic
      message.info('Business Continuity Plan version rollback - Feature coming soon');
      
    } catch (error) {
      console.error('❌ Business Continuity Plan rollback error:', error);
      addError('Business Continuity Plan rollback failed', error);
    }
  }, [addError]);

  // Show loading state
  if (isLoadingTemplate) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" style={{ marginBottom: '16px' }} />
          <Text>Loading Business Continuity Plan...</Text>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        padding: '20px'
      }}>
        <Alert
          message="Business Continuity Plan Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={onClose}>
              Return to My Crisis Resources
            </Button>
          }
        />
      </div>
    );
  }

  // Build iframe URL - EXACT SAME as Crisis Plan
  const iframeUrl = templateUrl ? 
    `/plan.html?file=${encodeURIComponent(templateUrl)}&planKey=${encodeURIComponent(planKey)}&cmtId=${encodeURIComponent(activeCmt?.id || '')}` 
    : null;

  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#f5f5f5',
      padding: '16px',
      gap: '16px'
    }}>
      {/* Header Card - EXACT REPLICA of PlanEditor */}
      <Card 
        size="small" 
        style={{ 
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <EditOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {planTitle}
              </Title>
              <Space style={{ marginTop: '4px' }}>
                <Tag 
                  icon={<SecurityScanOutlined />} 
                  color="blue"
                >
                  CMT: {activeCmt?.name || 'Unknown'}
                </Tag>
                {pdfReady && (
                  <Tag 
                    icon={<CheckCircleOutlined />} 
                    color="green"
                  >
                    {fieldCount} fields detected
                  </Tag>
                )}
              </Space>
            </div>
          </div>
          
          <Space>
            {/* Save status */}
            {isSaving ? (
              <Space>
                <Spin size="small" />
                <Text type="secondary">Saving...</Text>
              </Space>
            ) : lastSaved ? (
              <Text type="secondary">
                Saved {lastSaved.toLocaleTimeString()}
              </Text>
            ) : null}
            
            {/* Action buttons - EXACT REPLICA */}
            <Button
              icon={<SaveOutlined />}
              onClick={triggerSave}
              disabled={isSaving}
              size="small"
              style={{
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                color: 'white'
              }}
            >
              💾 Save Plan
            </Button>
            
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!pdfReady}
              size="small"
            >
              Export
            </Button>
            
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setShowVersionHistory(true)}
              disabled={!pdfReady}
              size="small"
            >
              Version History
            </Button>
            
            <Button onClick={onClose} size="small">
              Close
            </Button>
          </Space>
        </div>
      </Card>

      {/* PDF Editor Iframe - EXACT REPLICA */}
      <div style={{ flex: 1, position: 'relative' }}>
        {iframeUrl ? (
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            onLoad={handleIframeLoad}
            sandbox="allow-scripts allow-same-origin"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: '4px'
            }}
            title="Business Continuity Plan Editor"
          />
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <Alert
              message="Template not available"
              description="Please contact your administrator."
              type="warning"
              showIcon
            />
          </div>
        )}
        
        {/* Loading overlay */}
        {!iframeLoaded && iframeUrl && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1
          }}>
            <div style={{ textAlign: 'center' }}>
              <Spin size="large" style={{ marginBottom: '16px' }} />
              <Text>Loading Business Continuity Plan editor...</Text>
            </div>
          </div>
        )}
      </div>
      
      {/* 🎯 PRODUCTION FEATURES: VERSION HISTORY DRAWER - EXACT REPLICA */}
      <Drawer
        title={
          <Space>
            <HistoryOutlined />
            Version History
            {currentVersion && (
              <Tag color="blue">Current: v{currentVersion}</Tag>
            )}
          </Space>
        }
        placement="right"
        onClose={() => setShowVersionHistory(false)}
        open={showVersionHistory}
        width={400}
      >
        <Timeline>
          {versionHistory.map((version, index) => (
            <Timeline.Item
              key={version.id}
              color={index === 0 ? 'blue' : 'gray'}
              dot={index === 0 ? <CheckCircleOutlined style={{ color: '#1890ff' }} /> : <ClockCircleOutlined />}
            >
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Version {version.version}</Text>
                {index === 0 && <Tag color="blue" size="small" style={{ marginLeft: '8px' }}>Current</Tag>}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                {version.uploadedAt?.toLocaleString()} by {version.createdBy}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                Changes: {version.changesSummary?.changeCount || 0}
              </div>
              {version.fileName && (
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                  File: {version.fileName}
                </div>
              )}
              {index > 0 && (
                <Popconfirm
                  title="Rollback to this version?"
                  description="This will replace the current version. Are you sure?"
                  onConfirm={() => handleVersionRollback(version)}
                  okText="Yes, Rollback"
                  cancelText="Cancel"
                >
                  <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}>
                    <ReloadOutlined /> Rollback to this version
                  </Button>
                </Popconfirm>
              )}
            </Timeline.Item>
          ))}
        </Timeline>
        
        {versionHistory.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
            <HistoryOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
            <div>No version history available</div>
          </div>
        )}
      </Drawer>
      
      {/* 🎯 PRODUCTION FEATURES: CONFLICT RESOLUTION MODAL - EXACT REPLICA */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            Save Conflict Detected
          </Space>
        }
        open={showConflictModal}
        onCancel={() => setShowConflictModal(false)}
        footer={[
          <Button key="refresh" onClick={() => resolveConflict('refresh')}>
            <ReloadOutlined /> Refresh Page
          </Button>,
          <Button key="overwrite" type="primary" danger onClick={() => resolveConflict('overwrite')}>
            <SaveOutlined /> Force Save
          </Button>
        ]}
      >
        <Alert
          type="warning"
          message="Another user has modified this Business Continuity Plan while you were editing."
          description={
            <div>
              <p>You have two options:</p>
              <ul>
                <li><strong>Refresh Page:</strong> Load the latest version (you'll lose your changes)</li>
                <li><strong>Force Save:</strong> Overwrite the other user's changes with yours</li>
              </ul>
              <p style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
                Current ETag: {conflictInfo?.currentETag}<br/>
                Your ETag: {conflictInfo?.providedETag}
              </p>
            </div>
          }
          style={{ marginBottom: '16px' }}
        />
      </Modal>
    </div>
  );
}
